import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from 'services/auth/authService';
import { toast } from 'react-toastify';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import Spinner from 'components/common/Spinner';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { setAccessToken } from 'utils/auth/auth';
import { Eye, EyeOff } from 'lucide-react';

// Google icon inline SVG component
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    email: string;
    fullName: string;
    role: string;
    _id: string;
  };
  message?: string;
}

interface LoginProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ setIsLoggedIn }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // Load saved credentials
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedRememberMe && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const storeTokens = (data: AuthResponse) => {
    setAccessToken(data.accessToken);
    const isProduction = window.location.protocol === 'https:';
    const secureFlag = isProduction ? '; secure' : '';
    document.cookie = `refreshToken=${data.refreshToken}; path=/; max-age=31536000${secureFlag}; samesite=strict`;
    localStorage.setItem('justLoggedIn', 'true');
  };

  const saveRememberMe = () => {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
      localStorage.setItem('rememberedPassword', password);
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedPassword');
      localStorage.removeItem('rememberMe');
    }
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLocalLoading(true);
      setErrorMessage('');
      const response: AxiosResponse<AuthResponse> = await authService.login(email, password);

      if (response.status === 200 && response.data.accessToken) {
        storeTokens(response.data);
        saveRememberMe();
        setIsLoggedIn(true);
        navigate('/dashboard');
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || t('auth.login.invalidCredentials');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    try {
      setLocalLoading(true);
      setErrorMessage('');
      if (!credentialResponse.credential) {
        throw new Error('Google login failed: No credential provided');
      }
      const response: AxiosResponse<AuthResponse> = await authService.googleLogin(
        credentialResponse.credential,
        i18n.language
      );

      if (response.status === 200 && response.data.accessToken) {
        storeTokens(response.data);
        setIsLoggedIn(true);
        navigate('/dashboard');
      } else {
        throw new Error(response.data.message || 'Google login failed');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || t('auth.login.googleLoginFailed');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-geist transition-colors duration-300 ${
        isDark ? 'bg-fc-dark' : 'bg-fc-light'
      }`}
    >
      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">
        {/* Logo */}
        <Logomark className="w-20 h-20" />

        {/* Title + Description */}
        <div className="w-full text-center space-y-4">
          <h1
            className={`text-xl sm:text-2xl font-bold leading-7 sm:leading-8 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('auth.login.title')}
          </h1>
          <p
            className={`text-base font-normal leading-6 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.login.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="w-full space-y-4">
          {/* Email field */}
          <div className="space-y-1">
            <label
              htmlFor="email"
              className={`block text-base font-medium leading-6 ${
                isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
              }`}
            >
              {t('auth.login.emailLabel')}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t('auth.login.emailPlaceholder')}
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={{ '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties}
              className={`w-full px-3 py-2.5 text-base leading-6 rounded-fc outline-none transition-all ${
                isDark
                  ? 'bg-fc-dark-surface-input text-fc-dark-text-primary placeholder-fc-dark-text-muted fc-input-gradient-border-dark'
                  : 'bg-white text-fc-text-primary placeholder-fc-text-muted fc-input-gradient-border'
              }`}
            />
          </div>

          {/* Password field */}
          <div className="space-y-1">
            <label
              htmlFor="password"
              className={`block text-base font-medium leading-6 ${
                isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
              }`}
            >
              {t('auth.login.passwordLabel')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder={t('auth.login.passwordPlaceholder')}
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={{ '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties}
                className={`w-full px-3 py-2.5 pr-10 text-base leading-6 rounded-fc outline-none transition-all ${
                  isDark
                    ? 'bg-fc-dark-surface-input text-fc-dark-text-primary placeholder-fc-dark-text-muted fc-input-gradient-border-dark'
                    : 'bg-white text-fc-text-primary placeholder-fc-text-muted fc-input-gradient-border'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff size={18} className={isDark ? 'text-fc-dark-text-muted' : 'text-fc-text-muted'} />
                ) : (
                  <Eye size={18} className={isDark ? 'text-fc-dark-text-muted' : 'text-fc-text-muted'} />
                )}
              </button>
            </div>
          </div>

          {/* Stay signed in + Forgot password */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setRememberMe(!rememberMe)}
              className="flex items-center gap-2 group"
            >
              {/* Toggle Switch */}
              <div
                className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                  rememberMe
                    ? 'bg-fc-gradient-btn'
                    : isDark
                    ? 'bg-fc-switch-dark-bg'
                    : 'bg-fc-switch-bg'
                }`}
              >
                <div
                  className={`absolute top-[2px] w-4 h-4 rounded-full transition-transform duration-200 ${
                    rememberMe ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  } ${isDark ? 'bg-fc-switch-dark-knob' : 'bg-fc-switch-knob'}`}
                />
              </div>
              <span
                className={`text-sm font-medium leading-5 ${
                  isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
                }`}
              >
                {t('auth.login.rememberMe')}
              </span>
            </button>
            <Link
              to="/forgot-password"
              className="text-sm font-medium leading-5 text-fc-blue hover:text-fc-blue-dark transition-colors"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded">
              <p className="text-sm text-red-700 dark:text-red-400">{errorMessage}</p>
            </div>
          )}

          {/* Login Button - Gradient */}
          <button
            type="submit"
            disabled={localLoading}
            className="w-full h-12 bg-fc-gradient-btn text-white text-base font-medium leading-6 rounded-fc-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {localLoading ? (
              <>
                <Spinner size="small" color="white" />
                <span>{t('auth.login.loggingIn')}</span>
              </>
            ) : (
              t('auth.login.loginButton')
            )}
          </button>
        </form>

        {/* Separator */}
        <div className="w-full flex items-center gap-2">
          <div
            className={`flex-1 h-px ${
              isDark ? 'bg-fc-separator-dark' : 'bg-fc-separator'
            }`}
          />
          <span
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.login.orLoginWith')}
          </span>
          <div
            className={`flex-1 h-px ${
              isDark ? 'bg-fc-separator-dark' : 'bg-fc-separator'
            }`}
          />
        </div>

        {/* Google Login Button - Custom styled wrapper */}
        <div
          style={{ '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties}
          className={`w-full h-12 rounded-fc-lg text-base font-medium leading-6 flex items-center justify-center gap-2 relative overflow-hidden ${
            isDark
              ? 'bg-fc-dark-surface text-fc-dark-text-primary fc-input-gradient-border-dark'
              : 'bg-white text-fc-text-primary fc-input-gradient-border'
          }`}
        >
          {/* Custom visual layer */}
          <div className="flex items-center justify-center gap-2 pointer-events-none z-10">
            <GoogleIcon className="w-6 h-6" />
            <span>Continue with Google</span>
          </div>
          {/* Invisible Google Login overlay */}
          <div className="absolute inset-0 opacity-0 z-20 flex items-center justify-center [&>div]:!w-full [&>div]:!h-full [&_iframe]:!w-full [&_iframe]:!h-full">
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => {
                const errorMsg = t('auth.login.googleLoginFailed');
                setErrorMessage(errorMsg);
                toast.error(errorMsg);
              }}
              size="large"
              width="480"
              use_fedcm_for_prompt={true}
            />
          </div>
        </div>

        {/* Sign Up Link */}
        <p className="text-center">
          <span
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.login.noAccount')}{' '}
          </span>
          <Link
            to="/registration"
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-[#51A2FF]' : 'text-fc-blue'
            } hover:underline`}
          >
            {t('auth.login.signUp')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
